import { useEffect, useRef, useState } from 'react'
import { EXPERTISE_SERVICES } from './expertiseData'
import { FaArrowRightLong } from 'react-icons/fa6'
import { appUrl } from '@astro/config'

const AUTOPLAY_MS = 4400
const PROGRESS_MS = 4300
const SWAP_DELAY_MS = 200

function ServiceIcon({ service, className }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      {service.circle && <circle cx={service.circle.cx} cy={service.circle.cy} r={service.circle.r} />}
      {service.rect && <rect x={service.rect.x} y={service.rect.y} width={service.rect.width} height={service.rect.height} rx={service.rect.rx} />}
      {service.paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {service.polylines?.map((pts, i) => (
        <polyline key={i} points={pts.join(',')} />
      ))}
    </svg>
  )
}

export default function OurExpertiseSection() {
  const [current, setCurrent] = useState(0)
  const [displayedIndex, setDisplayedIndex] = useState(0)
  const [contentVisible, setContentVisible] = useState(true)
  const timerRef = useRef(null)
  const swapTimerRef = useRef(null)
  const progressRef = useRef(null)
  const pausedRef = useRef(false)
  const wrapRef = useRef(null)

  const stop = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const start = () => {
    stop()
    timerRef.current = window.setInterval(() => {
      if (!pausedRef.current) setCurrent((c) => (c + 1) % EXPERTISE_SERVICES.length)
    }, AUTOPLAY_MS)
  }

  const resetProgress = () => {
    const el = progressRef.current
    if (!el) return
    el.style.transition = 'none'
    el.style.width = '0%'
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!pausedRef.current) {
          el.style.transition = `width ${PROGRESS_MS}ms linear`
          el.style.width = '100%'
        }
      })
    })
  }

  useEffect(() => {
    start()
    return stop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setContentVisible(false)
    swapTimerRef.current = window.setTimeout(() => {
      setDisplayedIndex(current)
      setContentVisible(true)
    }, SWAP_DELAY_MS)
    if (!pausedRef.current) resetProgress()
    return () => window.clearTimeout(swapTimerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  const goTo = (idx) => {
    setCurrent(idx)
    stop()
    start()
  }

  const handleMouseEnter = () => {
    pausedRef.current = true
    stop()
    if (progressRef.current) progressRef.current.style.transition = 'none'
  }
  const handleMouseLeave = () => {
    pausedRef.current = false
    resetProgress()
    start()
  }

  const displayed = EXPERTISE_SERVICES[displayedIndex]

  return (
    <section
      id="our-expertise"
      className="relative overflow-hidden z-10"
      style={{ background: 'radial-gradient(ellipse 180% 100% at 78% 55%,rgba(200,30,30,0.95) 0%,rgba(90,5,5,0.98) 45%,rgba(28,0,0,1) 80%)', padding: '96px 0 108px' }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.14] bg-[radial-gradient(rgba(255,255,255,0.25)_1px,transparent_1.5px)] bg-size-[26px_26px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-20 right-0 h-140 w-140 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle,rgba(255,120,120,0.15),transparent 65%)' }} aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 left-12 h-72 w-72 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle,rgba(50,0,0,0.40),transparent 70%)' }} aria-hidden="true" />

      <div className="relative z-10 max-w-340 mx-auto px-6 lg:px-8">
        <div className="max-w-xl mx-auto text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-8" style={{ background: 'rgba(255,150,150,0.40)' }} />
            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.4em', color: '#FCA5A5' }}>Sacred Sciences</span>
            <span className="h-px w-8" style={{ background: 'rgba(255,150,150,0.40)' }} />
          </div>
          <h2 className="font-royal leading-tight mb-4" style={{ color: '#FBF3E4' }}>
            Ancient Wisdom for
            <br />
            <span style={{ background: 'linear-gradient(90deg,#FFFFFF,#FCA5A5,#F87171)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Every Life Question
            </span>
          </h2>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, maxWidth: '360px', margin: '0 auto' }}>
            Eight sacred disciplines. One unified purpose — to illuminate your path and align your stars.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 xl:gap-14 items-start md:px-8">
          <div className="flex flex-row gap-2 w-full lg:hidden overflow-x-auto pb-2 no-scrollbar">
            {EXPERTISE_SERVICES.map((s, i) => (
              <button
                key={s.chip}
                type="button"
                onClick={() => goTo(i)}
                style={{
                  appearance: 'none',
                  background: 'none',
                  border: i === current ? '1px solid rgba(255,150,150,0.70)' : '1px solid rgba(255,255,255,0.18)',
                  outline: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                  padding: '7px 15px',
                  borderRadius: '999px',
                  fontFamily: 'var(--font-heading),serif',
                  fontSize: '9.5px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: i === current ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
                  background2: undefined,
                  backgroundColor: i === current ? 'rgba(255,255,255,0.18)' : 'transparent',
                  whiteSpace: 'nowrap',
                  transition: 'border-color 0.25s,color 0.25s,background 0.25s',
                }}
              >
                {s.chip}
              </button>
            ))}
          </div>

          <nav className="hidden lg:flex flex-col shrink-0" style={{ width: '320px' }} aria-label="Expertise areas">
            {EXPERTISE_SERVICES.map((s, i) => {
              const active = i === current
              return (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => goTo(i)}
                  style={{
                    appearance: 'none',
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    margin: 0,
                    boxSizing: 'border-box',
                    width: '100%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    padding: active ? '14px 0 14px 0' : '14px 0 14px 14px',
                    borderTop: '1px solid rgba(255,255,255,0.10)',
                    borderBottom: i === EXPERTISE_SERVICES.length - 1 ? '1px solid rgba(255,255,255,0.10)' : undefined,
                    borderLeft: active ? '3px solid rgba(255,150,150,0.70)' : 'none',
                    position: 'relative',
                    transition: 'padding-left 0.28s,border-left 0.28s',
                  }}
                >
                  <span style={{ fontFamily: "'Marcellus',serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.15em', color: active ? '#FFFFFF' : 'rgba(255,150,150,0.48)', paddingTop: '3px', flexShrink: 0, minWidth: '22px', transition: 'color 0.28s' }}>
                    {s.num}
                  </span>
                  <span style={{ width: '1px', alignSelf: 'stretch', background: active ? 'rgba(255,150,150,0.55)' : 'rgba(255,150,150,0.18)', flexShrink: 0, transition: 'background 0.28s' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: 'var(--font-heading),serif', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: active ? '#FFFFFF' : 'rgba(255,255,255,0.52)', transition: 'color 0.28s', lineHeight: 1.3 }}>
                      {s.label}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '10px',
                        color: 'rgba(255,255,255,0.42)',
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        maxHeight: active ? '40px' : '0',
                        opacity: active ? 1 : 0,
                        marginTop: active ? '5px' : '0',
                        transition: 'max-height 0.32s ease,opacity 0.28s,margin-top 0.28s',
                      }}
                    >
                      {s.chip} · {s.features[0]}
                    </span>
                  </span>
                  <span style={{ fontSize: '15px', color: active ? 'rgba(255,200,200,0.90)' : 'rgba(255,150,150,0)', flexShrink: 0, paddingTop: '1px', transition: 'color 0.28s,transform 0.28s', transform: active ? 'translateX(4px)' : 'translateX(0)' }}>
                    ›
                  </span>
                </button>
              )
            })}
          </nav>

          <div className="flex-1 min-w-0" ref={wrapRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className="relative rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,150,150,0.15)', background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(0,0,0,0.30)', minHeight: '430px' }}>
              <div className="pointer-events-none select-none absolute font-royal font-bold leading-none" style={{ right: '24px', top: '12px', fontSize: 'clamp(78px,13vw,170px)', color: 'rgba(255,255,255,0.05)', transition: 'opacity 0.4s' }}>
                {displayed.num}
              </div>

              <svg className="pointer-events-none absolute inset-0 w-full h-full" style={{ opacity: 0.055 }} xmlns="http://www.w3.org/2000/svg" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
                <circle cx="85%" cy="15%" r="1.8" fill="#F87171" />
                <circle cx="74%" cy="26%" r="1.1" fill="#F87171" />
                <circle cx="90%" cy="36%" r="2.2" fill="#F87171" />
                <circle cx="66%" cy="20%" r="1" fill="#F87171" />
                <circle cx="80%" cy="50%" r="1.6" fill="#F87171" />
                <circle cx="92%" cy="63%" r="1.1" fill="#F87171" />
                <circle cx="70%" cy="70%" r="2" fill="#F87171" />
                <circle cx="58%" cy="57%" r="1.2" fill="#F87171" />
                <circle cx="77%" cy="82%" r="1" fill="#F87171" />
                <line x1="85%" y1="15%" x2="74%" y2="26%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="74%" y1="26%" x2="90%" y2="36%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="74%" y1="26%" x2="66%" y2="20%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="90%" y1="36%" x2="80%" y2="50%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="80%" y1="50%" x2="92%" y2="63%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="80%" y1="50%" x2="70%" y2="70%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="70%" y1="70%" x2="58%" y2="57%" stroke="#F87171" strokeWidth="0.5" />
                <line x1="70%" y1="70%" x2="77%" y2="82%" stroke="#F87171" strokeWidth="0.5" />
              </svg>

              <div className="pointer-events-none absolute inset-x-0 top-0 h-36" style={{ background: 'radial-gradient(ellipse 55% 100% at 20% 0%,rgba(255,100,100,0.12),transparent)' }} aria-hidden="true" />

              <div
                className="relative z-10 p-7 md:p-10"
                style={{ minHeight: '430px', transition: 'opacity 0.32s ease,transform 0.32s ease', opacity: contentVisible ? 1 : 0, transform: contentVisible ? 'translateY(0)' : 'translateY(12px)' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '22px' }}>
                  <div
                    style={{
                      width: '58px',
                      height: '58px',
                      borderRadius: '16px',
                      background: 'linear-gradient(145deg,rgba(255,150,150,0.18),rgba(255,100,100,0.06))',
                      border: '1px solid rgba(255,150,150,0.30)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#F87171',
                      flexShrink: 0,
                      boxShadow: '0 8px 28px rgba(0,0,0,0.30)',
                    }}
                  >
                    <ServiceIcon service={displayed} />
                  </div>
                  <div style={{ textAlign: 'right', paddingTop: '4px' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: 600, letterSpacing: '0.18em', color: 'rgba(255,180,180,0.72)' }}>
                      {displayed.num} <span style={{ color: 'rgba(255,255,255,0.30)' }}>/</span> 08
                    </div>
                    <div style={{ fontFamily: 'var(--font-heading),serif', fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.32em', color: 'rgba(255,255,255,0.50)', marginTop: '4px' }}>
                      Sacred Science
                    </div>
                  </div>
                </div>

                <h3 style={{ fontFamily: 'var(--font-heading),serif', fontSize: 'clamp(20px,3.2vw,32px)', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.2, marginBottom: '10px' }}>
                  {displayed.label}
                </h3>

                <div style={{ width: '36px', height: '2px', borderRadius: '99px', marginBottom: '14px', background: 'linear-gradient(90deg,#F87171,#EF4444,transparent)' }} />

                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.68)', lineHeight: 1.72, marginBottom: '20px' }}>{displayed.desc}</p>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {displayed.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11.5px', color: 'rgba(255,255,255,0.62)' }}>
                      <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#F87171', opacity: 0.8, flexShrink: 0 }} />
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href={appUrl("jyotish", "consultations")}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 22px',
                    borderRadius: '999px',
                    background: 'linear-gradient(135deg,rgba(255,255,255,0.12),rgba(255,255,255,0.05))',
                    border: '1px solid rgba(255,150,150,0.30)',
                    color: '#FFFFFF',
                    fontFamily: 'inherit',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                    transition: 'all 0.22s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg,rgba(255,255,255,0.22),rgba(255,255,255,0.10))'; e.currentTarget.style.borderColor = 'rgba(255,150,150,0.65)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg,rgba(255,255,255,0.12),rgba(255,255,255,0.05))'; e.currentTarget.style.borderColor = 'rgba(255,150,150,0.30)' }}
                >
                  Ask About This Service
                  <FaArrowRightLong size={18} />
                </a>
              </div>
            </div>

            <div className="mt-3 rounded-full overflow-hidden" style={{ height: '2px', background: 'rgba(255,255,255,0.10)' }}>
              <div ref={progressRef} style={{ height: '100%', borderRadius: '99px', background: 'linear-gradient(90deg,#7F1D1D,#F87171,#FECACA,#F87171,#7F1D1D)', width: '0%' }} />
            </div>

            <div className="flex items-center justify-center gap-2 mt-5">
              <div className="flex gap-1.5" role="tablist" aria-label="Service navigation">
                {EXPERTISE_SERVICES.map((s, i) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Go to ${s.label}`}
                    style={{
                      appearance: 'none',
                      border: 'none',
                      outline: 'none',
                      margin: 0,
                      padding: 0,
                      cursor: 'pointer',
                      width: i === current ? '18px' : '6px',
                      height: '6px',
                      display: 'inline-block',
                      borderRadius: i === current ? '3px' : '50%',
                      background: i === current ? '#FFFFFF' : 'rgba(255,255,255,0.28)',
                      transition: 'width 0.32s,border-radius 0.32s,background 0.32s',
                    }}
                  />
                ))}
              </div>
              <span className="ml-3 font-mono" style={{ fontSize: '10px', color: 'rgba(255,180,180,0.72)', letterSpacing: '0.14em' }}>
                {displayed.num} / 08
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
